import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingButton } from '@mui/lab'
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  GridProps,
  RadioGroup,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'

import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import errorHandlerHook from '@hooks/useErrorHandler'
import responsiveHook from '@hooks/useResponsive'

import ContactService from '@services/contact'
import LeadService from '@services/leads'

import { EmailSentFeedback } from '@components/ContactUs/components/EmailSentFeedback'
import { MaskTextField } from '@components/MaskTextField'
import { RadioButton } from '@components/RadioButton'
import { TextField } from '@components/TextField'

import { PHONE_MASK } from '@utils/phone'
import { ContactUserType, LeadSource, UserType } from '@utils/types'

import {
  contactUsSx,
  containerSx,
  emailAndPhoneContainerSx,
  firstAndLastNameContainerSx,
  formContainerSx,
  inputStyle,
  loadingButtonSx,
  messageSx,
  radioGroupSx,
  radioLabelSx,
  radioSectionSx,
  radioSectionTitle,
  submitButtonIconSx,
} from './styles'

type UserTypeFormRadioOptions = (UserType | 'other') | null

type FormData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  message: string
  type: UserTypeFormRadioOptions
}

const MAX_NAME_LENGTH = 64
const MAX_EMAIL_LENGTH = 80
const MAX_MESSAGE_LENGTH = 2048

const INITIAL_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: '',
  type: null,
}

interface IContactFormProps extends GridProps {
  containerOverrideSx?: SxProps<Theme>
}

const ContactForm = ({ containerOverrideSx, ...rest }: IContactFormProps) => {
  const { t } = useTranslation()
  const { isTabletOrBigger } = responsiveHook.useResponsive()

  const [isEmailSent, setIsEmailSent] = useState(false)

  const SCHEMA = Yup.object().shape({
    firstName: Yup.string()
      .required(t('Required'))
      .trim()
      .max(
        MAX_NAME_LENGTH,
        t('First name must be {{maxLengthName}} characters or less', {
          maxLengthName: MAX_NAME_LENGTH,
        })
      ),
    lastName: Yup.string()
      .required(t('Required'))
      .trim()
      .max(
        MAX_NAME_LENGTH,
        t('Last name must be {{maxLengthName}} characters or less', {
          maxLengthName: MAX_NAME_LENGTH,
        })
      ),
    email: Yup.string()
      .required(t('Required'))
      .trim()
      .email(t('Invalid email')),
    phone: Yup.string()
      .min(PHONE_MASK.length, t('Invalid phone number'))
      .max(PHONE_MASK.length, t('Invalid phone number')),
    message: Yup.string()
      .required(t('Required'))
      .trim()
      .max(
        MAX_MESSAGE_LENGTH,
        t('Message must be {{maxMessageLength}} characters or less', {
          maxMessageLength: MAX_MESSAGE_LENGTH,
        })
      ),
    type: Yup.string().required(t('Required')),
  })

  const getContactUserType = (userType: UserTypeFormRadioOptions) => {
    switch (userType) {
      case UserType.BUYER:
        return ContactUserType.BUYER
      case UserType.VENDOR:
        return ContactUserType.VENDOR
      default:
        return ContactUserType.OTHER
    }
  }

  const handleOnSubmit = async ({
    firstName,
    lastName,
    email,
    type,
    phone,
    message,
  }: FormData) => {
    try {
      const contactForm = {
        firstName,
        lastName,
        email,
        type: type!,
        phone,
        message,
      }

      await LeadService.create({
        lead: {
          ...contactForm,
          userType: getContactUserType(contactForm.type),
          companyEmail: contactForm.email,
        },
        source: LeadSource.CONTACT_FORM,
      })
      await ContactService.sendContact(contactForm)

      setIsEmailSent(true)
    } catch (err) {
      handleError({ exception: err })
    }
  }

  const formik = useFormik<FormData>({
    validationSchema: SCHEMA,
    validateOnBlur: false,
    validateOnMount: true,
    validateOnChange: true,
    initialValues: INITIAL_VALUES,
    onSubmit: handleOnSubmit,
  })

  const { handleError } = errorHandlerHook.useErrorHandler(formik)

  const handleOnSendAnotherMessageButtonClick = () => {
    setIsEmailSent(false)
    formik.resetForm()
  }

  const isSubmitButtonEnabled = formik.isValid && formik.dirty

  return (
    <Grid
      item
      xs={12}
      lg={6}
      sx={
        {
          ...containerSx,
          ...containerOverrideSx,
        } as SxProps<Theme>
      }
      {...rest}
    >
      {isEmailSent ? (
        <EmailSentFeedback
          onSendAnotherMessageButtonClick={
            handleOnSendAnotherMessageButtonClick
          }
        />
      ) : (
        <Stack
          component="form"
          onSubmit={formik.handleSubmit}
          sx={formContainerSx}
        >
          <Typography variant="h3" sx={contactUsSx}>
            {t('Contact us')}
          </Typography>

          <Box sx={firstAndLastNameContainerSx}>
            <TextField
              fullWidth
              aria-label={t('First Name')}
              label={t('First Name')}
              size="small"
              name="firstName"
              variant="filled"
              value={formik.values.firstName}
              error={!!formik.errors.firstName && !!formik.touched.firstName}
              helperText={
                formik.touched.firstName ? formik.errors.firstName : ''
              }
              inputProps={{
                'data-testid': 'first-name-input',
                style: inputStyle,
                maxLength: MAX_NAME_LENGTH,
              }}
              showMaxLengthInHelperText
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            <TextField
              fullWidth
              aria-label={t('Last Name')}
              label={t('Last Name')}
              size="small"
              name="lastName"
              variant="filled"
              value={formik.values.lastName}
              error={!!formik.errors.lastName && !!formik.touched.lastName}
              helperText={formik.touched.lastName ? formik.errors.lastName : ''}
              inputProps={{
                'data-testid': 'last-name-input',
                style: inputStyle,
                maxLength: MAX_NAME_LENGTH,
              }}
              showMaxLengthInHelperText
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Box>

          <Box sx={emailAndPhoneContainerSx}>
            <TextField
              fullWidth
              aria-label={t('Email')}
              label={t('Email')}
              size="small"
              name="email"
              variant="filled"
              value={formik.values.email}
              error={!!formik.errors.email && !!formik.touched.email}
              helperText={formik.touched.email ? formik.errors.email : ''}
              inputProps={{
                'data-testid': 'email-input',
                style: inputStyle,
                maxLength: MAX_EMAIL_LENGTH,
              }}
              showMaxLengthInHelperText
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <MaskTextField
              fullWidth
              mask={PHONE_MASK}
              aria-label={t('Phone Number (Optional)')}
              label={t('Phone Number (Optional)')}
              size="small"
              name="phone"
              variant="filled"
              value={formik.values.phone}
              type="tel"
              lazy
              error={!!formik.errors.phone && !!formik.touched.phone}
              helperText={formik.touched.phone ? formik.errors.phone : ''}
              inputProps={{
                'data-testid': 'phone-input',
                style: inputStyle,
              }}
              onValueChange={(value) => {
                formik.setFieldValue('phone', value)
                formik.setFieldTouched('phone', false)
              }}
              onBlur={() => formik.setFieldTouched('phone', true)}
            />
          </Box>

          <Stack sx={radioSectionSx}>
            <Typography sx={radioSectionTitle}>
              {t('Are you a (select one):')}
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row={isTabletOrBigger}
                aria-label={t('User type')}
                name="type"
                value={formik.values.type}
                sx={radioGroupSx}
                onChange={formik.handleChange}
              >
                <RadioButton
                  labelSx={radioLabelSx}
                  data-testid="it-customer-radio"
                  label={t('Tech Customer')}
                  value={UserType.BUYER}
                />
                <RadioButton
                  labelSx={radioLabelSx}
                  data-testid="technology-vendor-radio"
                  label={t('Technology Vendor')}
                  value={UserType.VENDOR}
                />
                <RadioButton
                  labelSx={radioLabelSx}
                  data-testid="other-radio"
                  label={t('Other')}
                  value="other"
                />
              </RadioGroup>
              {!!formik.errors.type && !!formik.touched.type && (
                <FormHelperText>{formik.values.type}</FormHelperText>
              )}
            </FormControl>
          </Stack>

          <TextField
            aria-label={t('Message')}
            label={t('Enter your message...')}
            name="message"
            variant="filled"
            size="medium"
            value={formik.values.message}
            error={!!formik.errors.message && !!formik.touched.message}
            helperText={formik.touched.message ? formik.errors.message : ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            multiline
            maxRows={8}
            inputProps={{
              'data-testid': 'message-input',
              maxLength: MAX_MESSAGE_LENGTH,
              style: inputStyle,
            }}
            sx={messageSx}
          />
          <LoadingButton
            type="submit"
            size="large"
            data-testid="submit-button"
            loading={formik.isSubmitting}
            disabled={!isSubmitButtonEnabled}
            variant={isSubmitButtonEnabled ? 'contained' : 'outlined'}
            sx={loadingButtonSx}
            endIcon={<ArrowForwardIcon sx={submitButtonIconSx} />}
          >
            {isSubmitButtonEnabled ? t('Submit') : t('Complete the form')}
          </LoadingButton>
        </Stack>
      )}
    </Grid>
  )
}

export { ContactForm }
